import { defineStore } from 'pinia'
import { ref } from 'vue'
import { httpClient } from '@/utils/http/client'
import type { FeatureFlag, FeatureFlagsResponse } from '@/types/featureFlags'
import type { AxiosError } from 'axios'

export const useFeatureFlagsStore = defineStore('featureFlags', () => {
  // State
  const flags = ref<Record<string, FeatureFlag>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Actions
  async function fetchFlags(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.get<FeatureFlagsResponse>('/feature-flags')
      flags.value = response.data.flags
      initialized.value = true
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to fetch feature flags'
      throw err
    } finally {
      loading.value = false
    }
  }

  function isEnabled(flagKey: string): boolean {
    const flag = flags.value[flagKey]
    return flag?.enabled ?? false
  }

  function getValue<T>(flagKey: string, defaultValue: T): T {
    const flag = flags.value[flagKey]
    if (!flag || !flag.enabled) {
      return defaultValue
    }
    return (flag.value as T) ?? defaultValue
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    flags.value = {}
    loading.value = false
    error.value = null
    initialized.value = false
  }

  return {
    // State
    flags,
    loading,
    error,
    initialized,
    // Actions
    fetchFlags,
    isEnabled,
    getValue,
    clearError,
    reset,
  }
})
